/**
 * @author zhongxinzhi
 * @date 2020/8/23 7:05 下午
 */
// @todo 参考clue发送埋点兼容
import Core, { IConfig, LogItem, shadowMerge, toQueryParams } from '@alife/cone-arms-core';
import { getWebUid, getMiniAppUid, isNotEmptyFunction, getSendInfo } from './util';
import jsPluginError from '@ali/trace-plugin-error';
import defaultSetting from '../setting.json';
import { getVersion, isDebug } from '../utils';

declare global {
  const wx: any;
  const my: any;
  const dd: any;
}

const sdk_version = defaultSetting.sdkVersion;

let instance: Core;

const TraceSdk = <T extends Core = Core>(config: IConfig, pipe?: any[]) => {
  const defaultConfig = {
    useSendBeacon: true,
    errorEnable: true,
    ignoreErrors: [],
    enableAllRejection: false,
    singleton: true,
  };
  const finalConfig = shadowMerge(defaultConfig, config);
  const pluginConfig = finalConfig.plugins || [];
  const { singleton } = finalConfig;

  const genFinalParams = (params: { [key: string]: any } = {}) => {
    const originParams = typeof params === 'object' && params ? params : {};
    const finalParams: Partial<LogItem> = {
      ...originParams,
      sdk_version,
      version: getVersion(originParams.version),
    };
    return finalParams;
  };

  // 检查数据大小
  const checkDataSize = (data: string): boolean => {
    const dataSize = new Blob([data]).size;
    const MAX_SIZE = 1024 * 1024; // 1MB
    if (dataSize > MAX_SIZE) {
      return false;
    }
    return true;
  };

  if (finalConfig.errorEnable === true) {
    // 将error插件放在最前面，确保error插件最先执行
    pluginConfig.unshift([
      jsPluginError,
      {
        ignoreErrors: finalConfig.ignoreErrors,
        enableAllRejection: finalConfig.enableAllRejection,
      },
    ]);
  }
  finalConfig.plugins = pluginConfig;
  if (singleton && instance) {
    return instance;
  } else {
    const core = new Core(finalConfig, pipe);
    if (singleton) {
      instance = core;
    }
    // 兼容小程序发送
    const miniApp =
      (typeof wx === 'object' && wx) ||
      (typeof my === 'object' && my) ||
      (typeof dd === 'object' && dd);

    if (miniApp && typeof miniApp.request === 'function' && isNotEmptyFunction(miniApp.request)) {
      // 覆盖获取uid的方法
      core.getUidFromCookie = getMiniAppUid;
      // 覆盖send方法
      core.send = (params: { [key: string]: any } = {}) => {
        const finalParams = genFinalParams(params);
        const sendInfo = getSendInfo(finalParams.type, config.aplusUrl);
        const { APLUS_URL } = sendInfo;
        const gokey = toQueryParams(finalParams, true);
        if (!checkDataSize(gokey)) {
          return;
        }
        miniApp.request({
          url: APLUS_URL,
          data: JSON.stringify({
            gmkey: 'OTHER',
            gokey,
            logtype: '2',
          }),
          method: 'post',
          dataType: 'base64',
          header: {
            'content-type': 'application/json', // 默认值
          },
          success() {},
        });
      };
    } else if (
      finalConfig.useSendBeacon &&
      typeof navigator === 'object' &&
      typeof navigator.sendBeacon === 'function'
    ) {
      // 覆盖获取uid的方法
      core.getUidFromCookie = getWebUid;
      // 覆盖send方法
      core.send = (params: { [key: string]: any }) => {
        const finalParams = genFinalParams(params);
        const sendInfo = getSendInfo(finalParams.type, config.aplusUrl);
        const { APLUS_URL } = sendInfo;
        const gokey = toQueryParams(finalParams, true);
        if (!checkDataSize(gokey)) {
          return;
        }
        const reportParams: Record<string, any> = {
          gmkey: 'OTHER',
          gokey,
          logtype: '2',
        };
        // debug模式下，将参数放到arms_params中, 便于调试
        if (isDebug()) {
          reportParams.arms_params = finalParams;
        }
        navigator.sendBeacon(APLUS_URL, JSON.stringify(reportParams, null, 0));
      };
    } else if (typeof window === 'object' && window && typeof document === 'object' && document) {
      // 覆盖获取uid的方法
      core.getUidFromCookie = getWebUid;
      // 覆盖send方法
      core.send = (params: { [key: string]: any }) => {
        const finalParams = genFinalParams(params);
        const sendInfo = getSendInfo(finalParams.type, config.aplusUrl);
        const { APLUS_URL } = sendInfo;
        const gokey = toQueryParams(finalParams, false);
        if (!checkDataSize(gokey)) {
          return;
        }
        const img = new Image();
        img.src = `${APLUS_URL}?${gokey}`;
      };
    }

    return core as T;
  }
};

export default TraceSdk;
